import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { ColorPicker, Input, Button, Form, Card, Space, Typography } from 'antd';
import type { Color as AntColor } from 'antd/es/color-picker';

const { Title } = Typography;

interface CreateColorProps {
  onSuccess?: (color: any) => void;
  onCancel?: () => void;
  isDrawerMode?: boolean;
}

const CreateColor: React.FC<CreateColorProps> = ({ onSuccess, onCancel, isDrawerMode = false }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentColorHex, setCurrentColorHex] = useState<string>('FFFFFF'); // Varsayılan beyaz, # olmadan

  const handleColorPickerChange = (colorValue: AntColor, hex?: string) => { // hex parametresini opsiyonel yapalım
    let effectiveHex: string | undefined = undefined;

    if (colorValue && typeof colorValue.toHexString === 'function') {
      try {
        effectiveHex = colorValue.toHexString(); // toHexString() #RRGGBB veya #RRGGBBAA dönebilir
        // console.log('[CreateColor - ColorPicker] colorValue.toHexString() sonucu:', effectiveHex);
      } catch (e) {
        console.error('[CreateColor - ColorPicker] colorValue.toHexString() çağrılırken hata:', e);
      }
    }

    // Eğer toHexString() başarısız olursa veya hex parametresi daha güvenilirse (bu senaryoda pek olası değil)
    // hex parametresini yedek olarak kullanabilirdik, ama önceliğimiz colorValue.
    if (!effectiveHex && typeof hex === 'string' && hex && hex !== '#') {
        // console.log('[CreateColor - ColorPicker] colorValue.toHexString() başarısız, hex parametresi kullanılıyor:', hex);
        effectiveHex = hex;
    }


    if (typeof effectiveHex !== 'string' || !effectiveHex || effectiveHex === '#') {
      console.warn('[CreateColor - ColorPicker] Geçerli bir hex değeri alınamadı. Gelen hex:', hex, 'colorValue:', colorValue);
      setCurrentColorHex('FFFFFF'); // Varsayılan renge dön
      form.setFieldsValue({ hexCode: '' }); // Formdaki hex kodunu temizle
      return;
    }

    let pureHex = effectiveHex.startsWith('#') ? effectiveHex.substring(1) : effectiveHex;
    pureHex = pureHex.toUpperCase();

    // Alfa (şeffaflık) kanalını kaldır (ColorPicker disabledAlpha olsa bile toHexString bazen gönderebilir)
    if (pureHex.length === 8) { // #RRGGBBAA formatı
      pureHex = pureHex.substring(0, 6);
    } else if (pureHex.length === 3) { // #RGB formatı
      pureHex = pureHex.split('').map(char => char + char).join('');
    }

    if (/^[0-9A-F]{6}$/.test(pureHex)) {
      setCurrentColorHex(pureHex);
      form.setFieldsValue({ hexCode: pureHex });
    } else {
      console.warn(`[CreateColor - ColorPicker] İşlenmiş hex geçerli değil: "${pureHex}". Orijinal hex: "${hex}", effectiveHex: "${effectiveHex}". Varsayılana dönülüyor.`);
      setCurrentColorHex('FFFFFF');
      form.setFieldsValue({ hexCode: '' });
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const dataToSubmit = {
      name: values.name,
      firmCode: values.firmCode || '',
      tcxCode: values.tcxCode || '',
      hexCode: values.hexCode || currentColorHex, // Formdan gelen hexCode'u veya anlık picker değerini kullan
      notes: values.notes || ''
    };

    try {
      const response = await api.post('/colors', dataToSubmit);
      toast.success('Renk başarıyla eklendi');
      if (onSuccess) {
        onSuccess(response.data);
      } else if (!isDrawerMode) {
        navigate('/colors');
      }
      form.resetFields();
      setCurrentColorHex('FFFFFF');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Renk oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const initialHex = form.getFieldValue('hexCode') || 'FFFFFF';

  return (
    <div className={`${isDrawerMode ? 'p-0' : 'bg-white rounded-lg shadow-md p-6'}`}>
      {!isDrawerMode && (
        <div className="flex justify-between items-center mb-6">
          <Title level={3} className="mb-0">Yeni Renk Ekle</Title>
          <Button onClick={() => navigate('/colors')}>
            Geri Dön
          </Button>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: '',
          firmCode: '',
          tcxCode: '',
          hexCode: '', // Başlangıçta boş
          notes: ''
        }}
      >
        <Card title="Temel Bilgiler" bordered={false} className="mb-6">
          <Form.Item
            name="name"
            label="Renk Adı"
            rules={[{ required: true, message: 'Lütfen renk adını girin!' }]}
          >
            <Input placeholder="Örn: Ateş Kırmızısı" />
          </Form.Item>

          <Form.Item
            name="firmCode"
            label="Firma Kodu (Opsiyonel)"
          >
            <Input placeholder="Firmaya özel renk kodu" />
          </Form.Item>
        </Card>

        <Card title="Renk Tanımları" bordered={false} className="mb-6">
          <Form.Item
            label="Görsel Renk Seçimi"
            help="Renk seçiciden bir renk belirleyin veya HEX kodunu girin."
          >
            <Space align="start">
              <ColorPicker
                value={`#${currentColorHex}`} // Doğrudan state'e bağla
                onChangeComplete={handleColorPickerChange}
                showText
                format="hex"
                disabledAlpha
              />

            </Space>
          </Form.Item>
    

          <Form.Item
            name="tcxCode"
            label="Pantone TCX Kodu (Opsiyonel)"
            help="Örn: 19-4052 TCX"
          >
            <Input placeholder="Pantone TCX kodu" />
          </Form.Item>
        </Card>

        <Card title="Ek Bilgiler" bordered={false} className="mb-6">
          <Form.Item
            name="notes"
            label="Notlar (Opsiyonel)"
          >
            <Input.TextArea rows={3} placeholder="Renk ile ilgili ek notlar" />
          </Form.Item>
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {loading ? 'Kaydediliyor...' : 'Renk Oluştur'}
            </Button>
            {(onCancel || !isDrawerMode) && (
              <Button onClick={onCancel || (() => navigate('/colors'))}>
                İptal
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateColor;